import { PageHeader } from "@mcleod/common";
import { Button, ButtonProps, Label, LabelVariant, Panel, PanelProps, Toast } from "@mcleod/components";
import {
    DeviceAccessor, GeneralSettings, getThemeColor,
    HorizontalAlignment, Stack, StringUtil, VerticalAlignment
} from "@mcleod/core";
import { CollapseController, CollapsingHeader, CollapsingHeaderHost } from "@mcleod/mobile-components";
import { AppPageHeaderProps } from "./AppPageHeaderProps";

const outerNavPanelPadding = 16;
const navButtonProps: Partial<ButtonProps> = {
    padding: 0,
    margin: 0,
    imageWidth: 24,
    imageHeight: 24
};
const commonNavProps: Partial<PanelProps> = {
    height: 60,
    rowBreak: false,
    wrap: false,
    verticalAlign: VerticalAlignment.CENTER
};

export class AppPageHeader extends PageHeader implements CollapsingHeaderHost {
    private _panelNavigation: Panel;
    private _panelNavigationLeft: Panel;
    private _panelNavigationCenter: Panel;
    private _panelNavigationRight: Panel;
    private _labelTitle: Label;
    private _title: string;
    private _collapsingHeaderStack: Stack<CollapsingHeader>;
    private _collapsingHeaderScrollListener: EventListener =
        (event: Event) => this.handleCollapsingHeaderOnScroll(event);

    private constructor(props?: Partial<AppPageHeaderProps>) {
        super({
            // ios overlays status bar on top of webview, so it needs some padding for the status bar.
            paddingTop: getHeaderPadding(),
            wrap: false,
            verticalAlign: VerticalAlignment.CENTER,
            ...props
        });
        CollapseController["controller"] = this;

        this.addMountListener(() => {
            const environmentMessage = GeneralSettings.get()?.environment_message;
            if (!StringUtil.isEmptyString(environmentMessage))
                Toast.showToast(environmentMessage);
        });
    }

    static get instance(): AppPageHeader {
        if (PageHeader.instance == null)
            new AppPageHeader();
        return PageHeader.instance as AppPageHeader;
    }

    protected override initComponents() {
        this.add(this.panelLogo, this.panelNavigation);
    }

    protected override get panelLogo(): Panel {
        const panel = super.panelLogo;
        panel.setProps({
            width: "100%",
            paddingBottom: 10,
            isRow: true,
            align: HorizontalAlignment.CENTER,
            verticalAlign: VerticalAlignment.BOTTOM
        });
        panel._element.style.position = "absolute";
        return panel;
    }

    protected override get logoImageHeight(): number {
        return 40;
    }

    get panelNavigation(): Panel {
        if (this._panelNavigation == null) {
            this._panelNavigation = Panel.createNoMarginNoPaddingPanel({
                id: "navContainer",
                isRow: true,
                ...commonNavProps
            });
            this._panelNavigation.add(
                this.panelNavigationLeft,
                this.panelNavigationCenter,
                this.panelNavigationRight
            );
        }
        return this._panelNavigation;
    }

    get panelNavigationLeft(): Panel {
        if (this._panelNavigationLeft == null) {
            this._panelNavigationLeft = Panel.createNoMarginNoPaddingPanel({
                id: "panelLeftNavigation",
                padding: outerNavPanelPadding,
                align: HorizontalAlignment.LEFT,
                verticalAlign: VerticalAlignment.CENTER,
                ...commonNavProps
            });
            this.syncNavigationLeft();
        }
        return this._panelNavigationLeft;
    }

    public syncNavigationLeft() {
        this.panelNavigationLeft.removeAll();
        const button = this.activeCollapsingHeader?.lhsNavigationButton;
        if (button != null) {
            button.setProps(navButtonProps);
            this.panelNavigationLeft.add(button);
        } else {
            this.syncMenuButton();
        }
    }

    get panelNavigationCenter(): Panel {
        if (this._panelNavigationCenter == null) {
            this._panelNavigationCenter = Panel.createNoMarginNoPaddingPanel({
                id: "panelNavigationCenter",
                fillRow: true,
                align: HorizontalAlignment.LEFT,
                ...commonNavProps
            });
            if (DeviceAccessor.isApp && !DeviceAccessor.isWeb) {
                this._panelNavigationCenter.addClickListener(() => {
                    window.location.href = "http://localhost:3000/mobile/app/AppTenantConnect";
                });
            }
            this._panelNavigationCenter.add(this.labelTitle);
            this.syncNavigationCenter();
        }
        return this._panelNavigationCenter;
    }

    public syncNavigationCenter() {
        const state = this.collapsingHeaderStack.peek();
        if (state != null)
            this._title = state.isTitleCollapsed ? state.title : null;
        else
            this._title = null;

        this.labelTitle.text = this._title || null;
        this.panelLogo.visible = this._title == null;
        this.labelTitle.visible = this._title != null;
    }

    private get panelNavigationRight(): Panel {
        if (this._panelNavigationRight == null) {
            this._panelNavigationRight = Panel.createNoMarginNoPaddingPanel({
                id: "panelNavigationRight",
                padding: outerNavPanelPadding,
                align: HorizontalAlignment.RIGHT,
                ...commonNavProps
            });
            this.syncNavigationRight();
        }
        return this._panelNavigationRight;
    }

    public syncNavigationRight() {
        this.panelNavigationRight.removeAll();
        const button = this.activeCollapsingHeader?.rhsActionButton;
        if (button != null) {
            button.setProps(navButtonProps);
            this.panelNavigationRight.add(button);
        }
    }

    public override syncNavigation() {
        this.syncHeader();
    }

    protected override syncHeader() {
        super.syncHeader();
        this.syncNavigationLeft();
        this.syncNavigationCenter();
        this.syncNavigationRight();
    }

    override get buttonMenu(): Button {
        const button = super.buttonMenu;
        button.setProps(navButtonProps);
        return button;
    }

    protected override syncMenuButton(menuButtonPanel: Panel = this.panelNavigationLeft) {
        super.syncMenuButton(menuButtonPanel);
    }

    private get labelTitle(): Label {
        if (this._labelTitle == null) {
            this._labelTitle = new Label({
                id: "labelPageHeaderTitle",
                variant: LabelVariant.pageTitle,
                paddingBottom: 0,
                paddingLeft: 0,
                color: this.companySettings.text_color ?? getThemeColor("primary.reverse"),
                fontBold: false,
                fillHeight: true,
                fillRow: true,
                maxWidth: window.innerWidth * 0.7,
                ...commonNavProps
            });
        }
        return this._labelTitle;
    }

    public collapseTitle(shouldCollapseTitle: boolean) {
        const state = this.collapsingHeaderStack.peek();
        if (state != null)
            state.isTitleCollapsed = shouldCollapseTitle;
        this.syncNavigationCenter();
    }

    private get collapsingHeaderStack(): Stack<CollapsingHeader> {
        if (this._collapsingHeaderStack == null)
            this._collapsingHeaderStack = new Stack<CollapsingHeader>()
        return this._collapsingHeaderStack;
    }

    public pushCollapsingHeader(header: CollapsingHeader) {
        this.collapsingHeaderStack.push(header);
        this.addCollapsingHeaderListeners();
        this.syncNavigation();
    }

    public popCollapsingHeader() {
        this.collapsingHeaderStack.pop();
        this.syncNavigation();
    }

    private get activeCollapsingHeader(): CollapsingHeader | null {
        return this.collapsingHeaderStack.peek();
    }

    private addCollapsingHeaderListeners() {
        const layout = this.activeCollapsingHeader?.layout;
        layout?.addLayoutLoadListener(() => {
            layout.addMountListener(() => {
                const scrollable = layout._element.parentElement
                scrollable?.addEventListener(
                    'scroll', this._collapsingHeaderScrollListener, { capture: true }
                );
            });

            layout.addUnmountListener(() => {
                const scrollable = layout._element.parentElement
                scrollable?.removeEventListener(
                    'scroll', this._collapsingHeaderScrollListener, { capture: true }
                );
                this.popCollapsingHeader();
            });
        });
    }

    private handleCollapsingHeaderOnScroll(event: Event): void {
        const layoutHeader = this.activeCollapsingHeader;

        if (layoutHeader != null) {
            layoutHeader.initialHeight ??= layoutHeader.bounds?.height ?? 0;
            const scrollTarget = event.target as HTMLElement;
            const scrollTop = scrollTarget.scrollTop;

            // Prevents collapse of header when scrolling on x-axis, if layout allows it.
            if (scrollTarget.scrollWidth > scrollTarget.clientWidth)
                return;

            const titlePanel = layoutHeader.panelTitle._element.getBoundingClientRect();
            const bottom = titlePanel.bottom;
            this.collapseTitle(bottom <= scrollTop);
        }
    }
}

function getHeaderPadding(): number {
    // TODO Long-term, this probably needs to change. Android will very
    //   likely have variable header paddings based on device type.
    //   iOS will be more normalized, but we may want to create a capacitor
    //   plugin to get the header height values that works for both platforms.
    if (DeviceAccessor.isiOSApp || DeviceAccessor.isAndroidApp)
        return 45;
    return 0;
}
